import React, { useState, useEffect, useCallback } from 'react';
import { Plus, QrCode, Eye, Trash2, RefreshCw, Loader2, ToggleLeft, ToggleRight, Copy, Power, ExternalLink, Package, DollarSign, ShoppingBag, X, Download, Printer, Check } from 'lucide-react';
import axios from 'axios';
import LowStockBanner from '../../../components/admin/LowStockBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPopupTab = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [items, setItems] = useState([]);
  const [qrModal, setQrModal] = useState(null);
  const [salesModal, setSalesModal] = useState(null);

  const fetchShops = useCallback(async () => {
    setLoading(true);
    try {
      const [shopsRes, itemsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/popup-shops`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/shop/items`, { withCredentials: true }),
      ]);
      setShops(shopsRes.data || []);
      setItems(itemsRes.data || []);
    } catch (e) {
      console.error('Failed to fetch popup shops', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchShops(); }, [fetchShops]);

  const toggleActive = async (shop) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/popup-shops/${shop.id}`, { active: !shop.active }, { withCredentials: true });
      fetchShops();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to toggle');
    }
  };

  const deleteShop = async (shop) => {
    if (!window.confirm(`Delete pop-up "${shop.name}"? Orders will be kept.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/popup-shops/${shop.id}`, { withCredentials: true });
      fetchShops();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openQr = async (shop) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/popup-shops/${shop.id}/qr`, { withCredentials: true });
      setQrModal({ shop, ...r.data });
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not generate QR');
    }
  };

  const openSales = async (shop) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/popup-shops/${shop.id}/sales`, { withCredentials: true });
      setSalesModal(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not load sales');
    }
  };

  const [copiedFor, setCopiedFor] = useState(null);
  const copyLink = async (shop) => {
    const url = `${window.location.origin}/popup/${shop.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedFor(shop.id);
      setTimeout(() => setCopiedFor(c => (c === shop.id ? null : c)), 2000);
    } catch {
      window.prompt('Copy this URL', url);
    }
  };

  const downloadQr = async (shop) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/popup-shops/${shop.id}/qr`, { withCredentials: true });
      const a = document.createElement('a');
      a.href = r.data.qr_image || r.data.qr_data_url;
      a.download = `popup-${shop.slug}-qr.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      alert(e.response?.data?.detail || 'Download failed');
    }
  };

  const printQrSign = async (shop) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/popup-shops/${shop.id}/qr`, { withCredentials: true });
      const qrSrc = r.data.qr_image || r.data.qr_data_url;
      const url = r.data.target_url || `${window.location.origin}/popup/${shop.slug}`;
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(`<!doctype html><html><head><title>${shop.name} — Pop-Up QR Sign</title>
        <style>
          @page { size: letter; margin: 0; }
          body { font-family: Georgia, 'Times New Roman', serif; margin: 0; padding: 0.5in; text-align: center; color: #0A0A0A; }
          .frame { border: 6px solid #BA1826; border-radius: 16px; padding: 0.6in; min-height: 9in; display: flex; flex-direction: column; justify-content: space-between; }
          h1 { font-size: 56px; margin: 0; color: #BA1826; }
          h2 { font-size: 28px; margin: 16px 0 4px; }
          p.scan { font-size: 36px; margin: 0; font-weight: bold; letter-spacing: 0.05em; }
          .qr { display: inline-block; padding: 24px; border: 4px solid #0A0A0A; border-radius: 12px; }
          img { width: 400px; height: 400px; display: block; }
          .url { font-family: monospace; font-size: 16px; word-break: break-all; }
          .foot { font-size: 14px; color: #4B5563; }
          @media print { body { padding: 0; } }
        </style></head><body>
        <div class="frame">
          <div>
            <h1>${shop.name}</h1>
            <h2>Novena Humanitarian Foundation</h2>
          </div>
          <div>
            <p class="scan">SCAN TO BUY</p>
            <div class="qr"><img src="${qrSrc}" alt="QR code"/></div>
            <p class="url">${url}</p>
          </div>
          <div class="foot">
            Apple Pay · Google Pay · Card · No app required<br/>
            100% of proceeds support our humanitarian programs.
          </div>
        </div>
        <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
      </body></html>`);
      w.document.close();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not generate print view');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-popup-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A]">Pop-Up Shops</h2>
          <p className="text-xs text-[#4B5563]">In-person QR-code checkout — Apple Pay, Google Pay, card. No shipping required.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchShops} className="text-xs bg-[#F1F5F9] text-[#0A0A0A] px-3 py-1.5 rounded flex items-center gap-1" data-testid="popup-refresh-btn">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button onClick={() => setShowCreate(true)} className="text-xs bg-[#BA1826] text-white px-3 py-1.5 rounded flex items-center gap-1 font-bold" data-testid="popup-create-btn">
            <Plus className="w-3 h-3" /> Create Pop-Up Shop
          </button>
        </div>
      </div>

      <LowStockBanner />

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#BA1826] animate-spin" /></div>
      ) : shops.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E7EB] rounded p-8 text-center">
          <p className="text-sm text-[#4B5563]">No pop-up shops yet. Create one to generate a QR code for in-person events.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {shops.map((s) => (
            <div key={s.id} className="bg-white border border-[#E5E7EB] rounded p-4" data-testid={`popup-card-${s.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-bold text-[#0A0A0A] truncate">{s.name}</p>
                  <p className="text-[10px] text-[#9CA3AF] font-mono">/popup/{s.slug}</p>
                </div>
                <button onClick={() => toggleActive(s)} className="flex-shrink-0" title={s.active ? 'Deactivate' : 'Activate'} data-testid={`popup-toggle-${s.id}`}>
                  {s.active
                    ? <ToggleRight className="w-7 h-7 text-green-600" />
                    : <ToggleLeft className="w-7 h-7 text-[#9CA3AF]" />}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1 text-center mb-3">
                <div className="bg-[#F8F9FA] rounded p-1.5"><p className="text-[9px] text-[#4B5563]">Items</p><p className="font-bold text-xs">{(s.item_ids || []).length}</p></div>
                <div className="bg-[#F8F9FA] rounded p-1.5"><p className="text-[9px] text-[#4B5563]">Sold</p><p className="font-bold text-xs">{s.items_sold || 0}</p></div>
                <div className="bg-[#F8F9FA] rounded p-1.5"><p className="text-[9px] text-[#4B5563]">Revenue</p><p className="font-bold text-xs text-[#BA1826]">${(s.revenue || 0).toFixed(2)}</p></div>
              </div>
              <div className="grid grid-cols-3 gap-1.5" data-testid={`popup-actions-${s.id}`}>
                <button onClick={() => openQr(s)} className="text-[11px] bg-[#0A0A0A] text-white px-2 py-2 rounded flex items-center justify-center gap-1 font-medium hover:bg-[#333]" data-testid={`popup-qr-${s.id}`}>
                  <QrCode className="w-3.5 h-3.5" /> View QR
                </button>
                <button onClick={() => downloadQr(s)} className="text-[11px] bg-[#F1F5F9] text-[#0A0A0A] px-2 py-2 rounded flex items-center justify-center gap-1 font-medium hover:bg-[#E5E7EB]" data-testid={`popup-download-qr-${s.id}`}>
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                <button onClick={() => printQrSign(s)} className="text-[11px] bg-[#F1F5F9] text-[#0A0A0A] px-2 py-2 rounded flex items-center justify-center gap-1 font-medium hover:bg-[#E5E7EB]" data-testid={`popup-print-qr-${s.id}`}>
                  <Printer className="w-3.5 h-3.5" /> Print Sign
                </button>
                <button onClick={() => copyLink(s)} className="text-[11px] bg-[#F1F5F9] text-[#0A0A0A] px-2 py-2 rounded flex items-center justify-center gap-1 font-medium hover:bg-[#E5E7EB]" data-testid={`popup-copy-link-${s.id}`}>
                  {copiedFor === s.id ? <><Check className="w-3.5 h-3.5 text-green-600" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy Link</>}
                </button>
                <a href={`/popup/${s.slug}`} target="_blank" rel="noopener noreferrer" className="text-[11px] bg-[#F1F5F9] text-[#0A0A0A] px-2 py-2 rounded flex items-center justify-center gap-1 font-medium hover:bg-[#E5E7EB]" data-testid={`popup-preview-${s.id}`}>
                  <Eye className="w-3.5 h-3.5" /> Preview
                </a>
                <button onClick={() => openSales(s)} className="text-[11px] bg-blue-600 text-white px-2 py-2 rounded flex items-center justify-center gap-1 font-medium hover:bg-blue-700" data-testid={`popup-sales-${s.id}`}>
                  <DollarSign className="w-3.5 h-3.5" /> View Sales
                </button>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#F1F5F9]">
                <button onClick={() => toggleActive(s)} className="text-[11px] flex items-center gap-1 font-semibold text-[#4B5563] hover:text-[#0A0A0A]" data-testid={`popup-toggle-action-${s.id}`}>
                  <Power className={`w-3.5 h-3.5 ${s.active ? 'text-green-600' : 'text-[#9CA3AF]'}`} />
                  {s.active ? 'Disable Pop-Up' : 'Enable Pop-Up'}
                </button>
                <button onClick={() => deleteShop(s)} className="text-[11px] text-red-600 hover:text-red-800 flex items-center gap-1" data-testid={`popup-delete-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreatePopupModal items={items} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchShops(); }} />}
      {qrModal && <QrModal data={qrModal} onClose={() => setQrModal(null)} />}
      {salesModal && <SalesModal data={salesModal} onClose={() => setSalesModal(null)} />}
    </div>
  );
};

// ===== Create Pop-Up Modal =====
const CreatePopupModal = ({ items, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [subtitle, setSubtitle] = useState('Choose your item and checkout quickly.');
  const [selectedItems, setSelectedItems] = useState({});  // { item_id: bool }
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { alert('Name is required'); return; }
    const item_ids = Object.entries(selectedItems).filter(([_, v]) => v).map(([k]) => k);
    if (item_ids.length === 0) { alert('Select at least one item'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/popup-shops`, {
        name: name.trim(),
        subtitle: subtitle.trim(),
        item_ids,
        active: true,
      }, { withCredentials: true });
      onCreated();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create');
    }
    setSaving(false);
  };

  const activeItems = items.filter((it) => it.enabled);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="popup-create-modal">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-4 flex items-center justify-between">
          <h3 className="font-heading text-base font-bold">Create Pop-Up Shop</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#9CA3AF]" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#4B5563] mb-1 block">Pop-Up Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Spring Festival Pop-Up"
              className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
              data-testid="popup-create-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#4B5563] mb-1 block">Subtitle</label>
            <input type="text" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} maxLength={120}
              className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
              data-testid="popup-create-subtitle-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#4B5563] mb-2 block">Select items for this pop-up</label>
            {activeItems.length === 0 ? (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">No enabled shop items exist. Create items in the Shop tab first.</p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto border border-[#E5E7EB] rounded p-2">
                {activeItems.map((it) => {
                  const remaining = it.inventory > 0 ? it.inventory - (it.sold_qty || 0) : 'unlimited';
                  return (
                    <label key={it.id} className="flex items-center gap-2 p-2 hover:bg-[#F8F9FA] rounded cursor-pointer">
                      <input type="checkbox" checked={!!selectedItems[it.id]}
                        onChange={(e) => setSelectedItems((p) => ({ ...p, [it.id]: e.target.checked }))}
                        data-testid={`popup-item-select-${it.id}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{it.name}</p>
                        <p className="text-[10px] text-[#9CA3AF]">${it.price.toFixed(2)} · {remaining === 'unlimited' ? 'No stock limit' : `${remaining} in stock`}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-[#9CA3AF] mt-1">Stock comes from the existing item inventory. To set a special inventory for this pop-up, update the item directly in the Shop tab.</p>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E5E7EB] p-4 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-4 py-2 text-[#4B5563]">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="text-xs bg-[#BA1826] text-white px-4 py-2 rounded font-bold disabled:opacity-50 flex items-center gap-1" data-testid="popup-create-save-btn">
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Create & Generate QR
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== QR Modal =====
const QrModal = ({ data, onClose }) => {
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(data.target_url); alert('URL copied!'); } catch { alert('Copy failed'); }
  };
  const downloadQr = () => {
    const a = document.createElement('a');
    a.href = data.qr_data_url;
    a.download = `popup-qr-${data.shop.slug}.png`;
    a.click();
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="popup-qr-modal">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-heading text-base font-bold">{data.shop.name}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#9CA3AF]" /></button>
        </div>
        <div className="bg-white p-4 rounded-lg border border-[#E5E7EB] flex flex-col items-center">
          <img src={data.qr_image || data.qr_data_url} alt="QR Code" className="w-48 h-48" data-testid="popup-qr-image" />
          <p className="text-[10px] text-[#9CA3AF] font-mono mt-2 text-center break-all">{data.target_url}</p>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={copyUrl} className="flex-1 text-xs bg-[#F1F5F9] text-[#0A0A0A] py-2 rounded flex items-center justify-center gap-1" data-testid="popup-qr-copy-btn">
            <Copy className="w-3 h-3" /> Copy URL
          </button>
          <button onClick={downloadQr} className="flex-1 text-xs bg-[#0A0A0A] text-white py-2 rounded flex items-center justify-center gap-1" data-testid="popup-qr-download-btn">
            <QrCode className="w-3 h-3" /> Download
          </button>
        </div>
        <a href={data.target_url} target="_blank" rel="noopener noreferrer" className="block text-center text-[10px] text-blue-600 hover:underline mt-3" data-testid="popup-qr-preview-link">
          Open preview <ExternalLink className="w-3 h-3 inline" />
        </a>
      </div>
    </div>
  );
};

// ===== Sales Modal =====
const SalesModal = ({ data, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="popup-sales-modal">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-4 flex justify-between items-center">
          <h3 className="font-heading text-base font-bold">{data.popup.name} — Live Sales</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#9CA3AF]" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Completed" value={data.totals.completed} icon={ShoppingBag} color="text-green-600" />
            <Stat label="Items Sold" value={data.totals.items_sold} icon={Package} color="text-[#0A0A0A]" />
            <Stat label="Revenue" value={`$${data.totals.revenue.toFixed(2)}`} icon={DollarSign} color="text-[#BA1826]" />
            <Stat label="Pending" value={data.totals.pending} icon={Power} color="text-amber-500" />
          </div>

          {data.by_item.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-2">By Item</p>
              <div className="border border-[#E5E7EB] rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#F8F9FA]"><tr><th className="text-left py-1.5 px-2">Item</th><th className="text-right py-1.5 px-2">Qty</th><th className="text-right py-1.5 px-2">Revenue</th></tr></thead>
                  <tbody>
                    {data.by_item.map((it, i) => (
                      <tr key={i} className="border-t border-[#F1F5F9]">
                        <td className="py-1 px-2">{it.name}</td>
                        <td className="py-1 px-2 text-right font-mono">{it.quantity}</td>
                        <td className="py-1 px-2 text-right font-mono">${it.revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-2">Recent Orders</p>
            {data.orders.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] italic">No orders yet.</p>
            ) : (
              <div className="border border-[#E5E7EB] rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#F8F9FA]"><tr><th className="text-left py-1.5 px-2">Order</th><th className="text-left py-1.5 px-2">Buyer</th><th className="text-center py-1.5 px-2">Status</th><th className="text-right py-1.5 px-2">Total</th></tr></thead>
                  <tbody>
                    {data.orders.slice(0, 20).map((o, i) => (
                      <tr key={i} className="border-t border-[#F1F5F9]">
                        <td className="py-1 px-2 font-mono text-[10px]">{o.order_number}</td>
                        <td className="py-1 px-2">{o.buyer_name}</td>
                        <td className="py-1 px-2 text-center">
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${o.status === 'completed' ? 'bg-green-100 text-green-700' : o.status === 'pending' ? 'bg-amber-100 text-amber-700' : o.status === 'refunded' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{o.status}</span>
                        </td>
                        <td className="py-1 px-2 text-right font-mono">${(o.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[10px] text-[#9CA3AF] mt-2 italic">For refunds, open the order in the Shop tab → Orders.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, icon: Icon, color }) => (
  <div className="bg-[#F8F9FA] rounded p-3 text-center">
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <p className={`font-heading text-lg font-bold ${color}`}>{value}</p>
    <p className="text-[9px] text-[#4B5563] uppercase tracking-wider">{label}</p>
  </div>
);

export default AdminPopupTab;
